/** Outer strip layout shared between types containing title, 'view all' */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiNetworkbarStrip } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import Button from '@wix/da-shared-react/pkg/Button';

import s from './StripWrapper.scss';

const SEE_ALL_BI_EVENT_DATA = {
  evid: BiEventType.SEE_ALL_CLICK,
};

const TITLE_PREFIX_KEYS = {
  explore: 'curated.strip.prefix.explore',
  from: 'curated.strip.prefix.from',
  welcome: 'curated.strip.prefix.welcome',
};
const LINK_LABEL_KEYS = {
  learn_more: 'facetPage.strip.link.learn_more',
  view_all: 'facetPage.strip.link.view_all',
  submit: 'facetPage.strip.link.submit',
};
const DEFAULT_TITLE_PREFIX_KEY = TITLE_PREFIX_KEYS['explore'];
const DEFAULT_LINK_LABEL_KEY = LINK_LABEL_KEYS['view_all'];

export interface Props {
  /** stream data and initial array of content and url pointing to larger collection */
  strip: PapiNetworkbarStrip;
  isSearchPage?: boolean;
  className?: string;
}

export const StripWrapper: React.FC<Props> = ({
  strip,
  isSearchPage,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const showTitle = strip.titleType !== 'none';
  const showLink = strip.urlType !== 'none';
  const titlePrefixKey =
    TITLE_PREFIX_KEYS[strip.titleType] || DEFAULT_TITLE_PREFIX_KEY;
  const linkLabelKey = LINK_LABEL_KEYS[strip.urlType] || DEFAULT_LINK_LABEL_KEY;
  const titleIsLink = strip.urlType !== 'submit';

  return (
    <BiLoggerContextProvider
      value={{
        sectionname: strip.title,
        sectiontype: strip.codename,
        _rankOffset: 0,
      }}
      key={strip.title}
    >
      <div className={className}>
        {(showTitle || showLink) && (
          <div
            className={classnames(
              s['top-row'],
              isSearchPage && s['search-result']
            )}
          >
            {showTitle && (
              <div className={s['title-and-results']}>
                <h2 className={s['title']}>
                  {!isSearchPage && (
                    <span className={s['title-prefix']}>
                      {t(titlePrefixKey)}{' '}
                    </span>
                  )}

                  {titleIsLink ? (
                    <BiLink
                      href={strip.url}
                      biData={SEE_ALL_BI_EVENT_DATA}
                      className={s['title-link']}
                    >
                      {strip.title}
                    </BiLink>
                  ) : (
                    <span className={s['title-link']}>{strip.title}</span>
                  )}

                  {strip.estTotal && (
                    <span className={s['results-count']}>
                      {isMobile
                        ? shortenNumber(strip.estTotal)
                        : t('facetPage.strip.estTotal', {
                            count: strip.estTotal,
                            formattedCount: shortenNumber(strip.estTotal),
                          })}
                    </span>
                  )}
                </h2>
              </div>
            )}

            {showLink && (
              <Button
                view="b33"
                href={strip.url}
                className={s['link']}
                biData={SEE_ALL_BI_EVENT_DATA}
              >
                <span>{t(linkLabelKey)}</span>
              </Button>
            )}
          </div>
        )}
        <div>{children}</div>
      </div>
    </BiLoggerContextProvider>
  );
};
StripWrapper.displayName = 'StripWrapper';

export default StripWrapper;
